import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import StatusBadge from '../components/StatusBadge';
import { Calendar, ChevronLeft, ChevronRight, MapPin, Plus, List, CheckCircle, Phone, X, LayoutGrid } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import ServiceDetailDrawer from '../components/ServiceDetailDrawer';
import { useAuth } from '../context/AuthContext';

const PROVINCE_MAP = {
  'Ontario': 'ON', 'Quebec': 'QC', 'British Columbia': 'BC',
  'Alberta': 'AB', 'Manitoba': 'MB', 'Saskatchewan': 'SK',
  'Nova Scotia': 'NS', 'New Brunswick': 'NB',
  'Newfoundland and Labrador': 'NL', 'Prince Edward Island': 'PE',
};

function SvcAddressAutocomplete({ onSelect, inputCls }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const wrapRef = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const search = (val) => {
    setQuery(val);
    clearTimeout(timerRef.current);
    if (val.length < 4) { setResults([]); setOpen(false); return; }
    timerRef.current = setTimeout(async () => {
      try {
        const r = await fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(val)}&limit=6&lang=en&countrycode=ca`);
        const data = await r.json();
        const items = (data.features || []).filter(f => f.properties?.street || f.properties?.name).slice(0, 5);
        setResults(items); setOpen(items.length > 0);
      } catch { }
    }, 320);
  };
  const pick = (f) => {
    const p = f.properties;
    const [lng, lat] = f.geometry.coordinates;
    const houseNum = p.housenumber ? `${p.housenumber} ` : '';
    const street = p.street || p.name || '';
    const address = `${houseNum}${street}`.trim();
    const city = p.city || p.town || p.village || p.county || '';
    const province = PROVINCE_MAP[p.state] || p.state || 'ON';
    onSelect({ address, city, province, lat, lng });
    setQuery(address ? `${address}, ${city}` : city);
    setOpen(false);
  };
  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
        <input className={`${inputCls} pl-9`} value={query} onChange={e => search(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)} placeholder="Search address…" autoComplete="off" />
      </div>
      {open && (
        <ul className="absolute z-50 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-60 overflow-auto text-sm">
          {results.map((f, i) => {
            const p = f.properties;
            const houseNum = p.housenumber ? `${p.housenumber} ` : '';
            const street = p.street || p.name || '';
            const city = p.city || p.town || p.village || p.county || '';
            return (
              <li key={i} className="px-3 py-2.5 hover:bg-orange-50 cursor-pointer border-b last:border-0"
                onMouseDown={() => pick(f)}>
                <span className="font-medium">{houseNum}{street}</span>
                {city && <span className="text-gray-500"> — {city}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// Fix Leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function toLocalDate(dateStr) {
  // Parse YYYY-MM-DD without timezone shift
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function formatDate(dateStr) {
  const d = toLocalDate(dateStr);
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
}

function formatDateFull(dateStr) {
  const d = toLocalDate(dateStr);
  return d.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatMapDate(dateStr) {
  const d = toLocalDate(dateStr);
  const weekdays = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const day = d.getDate();
  const suffix = [11,12,13].includes(day) ? 'th' : day % 10 === 1 ? 'st' : day % 10 === 2 ? 'nd' : day % 10 === 3 ? 'rd' : 'th';
  return `${weekdays[d.getDay()]}, ${months[d.getMonth()]} ${day}${suffix}`;
}

function getWeekStart(dateStr) {
  const d = toLocalDate(dateStr);
  const day = d.getDay(); // 0=Sun
  d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function addDays(dateStr, n) {
  const d = toLocalDate(dateStr);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Build month calendar grid (Mon-start, 6 rows × 7 cols)
function getMonthGrid(year, month) {
  const firstDay = new Date(year, month - 1, 1);
  let startDow = firstDay.getDay(); // 0=Sun
  if (startDow === 0) startDow = 7;
  const leadDays = startDow - 1;
  const lastDay = new Date(year, month, 0).getDate();
  const pad = n => String(n).padStart(2, '0');
  const iso = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  const cells = [];
  for (let i = leadDays; i > 0; i--) {
    cells.push({ dateStr: iso(new Date(year, month-1, 1-i)), current: false });
  }
  for (let d = 1; d <= lastDay; d++) {
    cells.push({ dateStr: iso(new Date(year, month-1, d)), current: true });
  }
  const tail = cells.length % 7 === 0 ? 0 : 7 - (cells.length % 7);
  for (let i = 1; i <= tail; i++) {
    cells.push({ dateStr: iso(new Date(year, month, i)), current: false });
  }
  return cells;
}

const emptySvcForm = {
  id: null,
  sale_id: '',
  contact_name: '',
  address: '',
  city: '',
  province: 'ON',
  lat: '',
  lng: '',
  phone: '',
  service_type: 'service',
  description: '',
  crew_notes: '',
  scheduled_date: '',
  scheduled_time_start: '',
};

export default function Schedule() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const highlightSaleId = Number(searchParams.get('highlight')) || null;
  const [jobs, setJobs] = useState([]);           // week view jobs
  const [monthJobs, setMonthJobs] = useState([]); // month view jobs
  const [upcomingAll, setUpcomingAll] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const highlightRef = useRef(null);

  // Month state: tracks first-of-month for the displayed month
  const [monthDate, setMonthDate] = useState(() => {
    const dateParam = new URLSearchParams(window.location.search).get('date');
    if (dateParam) {
      const [y, m] = dateParam.split('-').map(Number);
      return new Date(y, m - 1, 1);
    }
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });

  const [weekStart, setWeekStart] = useState(() => {
    const dateParam = new URLSearchParams(window.location.search).get('date');
    return getWeekStart(dateParam && dateParam >= todayStr() ? dateParam : todayStr());
  });
  const [view, setView] = useState('month'); // 'month' | 'week' | 'list' | 'map'
  const [showForm, setShowForm] = useState(!!(searchParams.get('sale_id') || searchParams.get('date')));
  const [unscheduledSales, setUnscheduledSales] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [routeSuggestions, setRouteSuggestions] = useState(null);
  const [loadingRoute, setLoadingRoute] = useState(false);
  const [form, setForm] = useState({
    sale_id: searchParams.get('sale_id') || '',
    scheduled_date: searchParams.get('date') || '',
    scheduled_time_start: '',
    crew_notes: '',
    payment_terms: '',
  });

  // Service scheduling state
  const [formType, setFormType] = useState('install'); // 'install' | 'service'
  const [svcForm, setSvcForm] = useState(emptySvcForm);
  const [svcCustomerMode, setSvcCustomerMode] = useState('existing'); // 'existing' | 'new'
  const [svcSaleSearch, setSvcSaleSearch] = useState('');
  const [installedSales, setInstalledSales] = useState([]);
  const [svcSubmitting, setSvcSubmitting] = useState(false);
  const [viewingServiceId, setViewingServiceId] = useState(null);

  const today = todayStr();

  const loadWeek = () => api.get(`/schedule/week/${weekStart}`).then(setJobs).catch(() => {});
  const loadAll = () => api.get('/schedule?start_date=' + today).then(setUpcomingAll).catch(() => {});

  const loadMonth = (date) => {
    const d = date || monthDate;
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    const pad = n => String(n).padStart(2, '0');
    const start = `${y}-${pad(m)}-01`;
    const lastDay = new Date(y, m, 0).getDate();
    const end = `${y}-${pad(m)}-${pad(lastDay)}`;
    api.get(`/schedule?start_date=${start}&end_date=${end}`).then(setMonthJobs).catch(() => {});
  };

  useEffect(() => { loadWeek(); }, [weekStart]);
  useEffect(() => { loadMonth(); }, [monthDate]);
  useEffect(() => {
    loadAll();
    api.get('/schedule/route-suggest').then(setSuggestions).catch(() => {});
    api.get('/sales?status=confirmed').then(sales => {
      setUnscheduledSales((sales || []).filter(s => s.status === 'confirmed'));
    }).catch(() => {});
  }, []);

  // Scroll to highlighted job after data loads
  useEffect(() => {
    if (highlightSaleId && (jobs.length > 0 || upcomingAll.length > 0) && highlightRef.current) {
      setTimeout(() => highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 200);
    }
  }, [highlightSaleId, jobs, upcomingAll]);

  // Fetch route suggestions when a sale is selected
  useEffect(() => {
    if (!form.sale_id) { setRouteSuggestions(null); return; }
    setLoadingRoute(true);
    api.get(`/schedule/suggest-date?sale_id=${form.sale_id}`)
      .then(setRouteSuggestions)
      .catch(() => setRouteSuggestions(null))
      .finally(() => setLoadingRoute(false));
  }, [form.sale_id]);

  const [geocodeStatus, setGeocodeStatus] = useState(null);
  const [geocoding, setGeocoding] = useState(false);
  const [allSales, setAllSales] = useState([]);
  const [serviceJobs, setServiceJobs] = useState([]);
  const [editingBase, setEditingBase] = useState(false);
  const [baseSearch, setBaseSearch] = useState('');
  const [baseResults, setBaseResults] = useState([]);
  const baseSearchTimer = useRef(null);

  useEffect(() => {
    api.get('/schedule/geocode-status').then(setGeocodeStatus).catch(() => {});
    api.get('/sales').then(data => setAllSales(data || [])).catch(() => {});
    api.get('/service').then(data => setServiceJobs(data || [])).catch(() => {});
    api.get('/sales').then(data => {
      setInstalledSales((data || []).filter(s =>
        ['completed', 'scheduled', 'in_progress', 'awaiting_payment'].includes(s.status)
      ));
    }).catch(() => {});
  }, []);

  const searchBase = (val) => {
    setBaseSearch(val);
    clearTimeout(baseSearchTimer.current);
    if (val.length < 4) { setBaseResults([]); return; }
    baseSearchTimer.current = setTimeout(async () => {
      const r = await fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(val)}&limit=5&lang=en`);
      const data = await r.json();
      setBaseResults(data.features || []);
    }, 320);
  };

  const pickBase = async (feature) => {
    const p = feature.properties;
    const [lng, lat] = feature.geometry.coordinates;
    const name = [p.name || p.street, p.city || p.town, p.state].filter(Boolean).join(', ');
    await api.put('/schedule/home-base', { lat, lng, name });
    const status = await api.get('/schedule/geocode-status');
    setGeocodeStatus(status);
    setEditingBase(false);
    setBaseSearch('');
    setBaseResults([]);
  };

  const handleGeocodeAll = async () => {
    setGeocoding(true);
    try {
      await api.post('/schedule/geocode-all', {});
      const status = await api.get('/schedule/geocode-status');
      setGeocodeStatus(status);
    } finally {
      setGeocoding(false);
    }
  };

  // Pre-fill payment terms when a sale is selected
  useEffect(() => {
    if (!form.sale_id) return;
    const sale = unscheduledSales.find(s => s.id === Number(form.sale_id));
    if (!sale) return;

    const fmtMoney = n => '$' + Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const totalPaid = sale.total_paid || 0;
    const remaining = sale.deal_total - totalPaid;
    const methodLabel = { cash: 'Cash', etransfer: 'E-Transfer', cc: 'Credit Card', financed: 'Financed (In-House)' }[sale.payment_type] || sale.payment_type;

    const lines = [];
    lines.push(`Method: ${methodLabel}`);
    lines.push(`Deal total: ${fmtMoney(sale.deal_total)}`);
    if (totalPaid > 0) lines.push(`Collected so far: ${fmtMoney(totalPaid)}`);
    if (sale.deposit_amount > 0 && totalPaid === 0) lines.push(`Deposit: ${fmtMoney(sale.deposit_amount)} (not yet collected)`);

    if (sale.payment_type === 'financed') {
      if (sale.finance_monthly_amount) lines.push(`Monthly payments: ${fmtMoney(sale.finance_monthly_amount)}/mo × ${sale.finance_term_months || '?'} months`);
      if (sale.finance_notes) lines.push(`Finance notes: ${sale.finance_notes}`);
      lines.push(`Remaining balance: ${fmtMoney(remaining)}`);
    } else if (sale.payment_type === 'cash') {
      if (sale.remaining_after_install > 0) {
        lines.push(`Due on install: ${fmtMoney(sale.remaining_after_install)}`);
        const beforeInstall = remaining - sale.remaining_after_install;
        if (beforeInstall > 0) lines.push(`Balance before install: ${fmtMoney(beforeInstall)}`);
      } else {
        lines.push(`Remaining: ${fmtMoney(remaining)}`);
      }
    } else {
      lines.push(`Remaining: ${fmtMoney(remaining)}`);
    }

    setForm(f => ({ ...f, payment_terms: lines.join(' | ') }));
  }, [form.sale_id]);

  const shiftWeek = (dir) => setWeekStart(w => addDays(w, dir * 7));
  const goToday = () => {
    setWeekStart(getWeekStart(today));
    const n = new Date();
    setMonthDate(new Date(n.getFullYear(), n.getMonth(), 1));
  };
  const shiftMonth = (dir) => setMonthDate(d => new Date(d.getFullYear(), d.getMonth() + dir, 1));

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  // Month view calculations (always computed, only rendered when view === 'month')
  const monthYear = monthDate.getFullYear();
  const monthNum = monthDate.getMonth() + 1;
  const monthCells = getMonthGrid(monthYear, monthNum);
  const jobsByDate = {};
  monthJobs.forEach(j => {
    if (!jobsByDate[j.scheduled_date]) jobsByDate[j.scheduled_date] = [];
    jobsByDate[j.scheduled_date].push(j);
  });
  // Service jobs grouped by date (scheduled only)
  const svcByDate = {};
  serviceJobs.filter(s => s.scheduled_date && s.status === 'scheduled').forEach(s => {
    if (!svcByDate[s.scheduled_date]) svcByDate[s.scheduled_date] = [];
    svcByDate[s.scheduled_date].push(s);
  });

  const handleSchedule = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/schedule', form);
      // Stay on month view; navigate to the month of the scheduled date
      if (form.scheduled_date) {
        const [y, m] = form.scheduled_date.split('-').map(Number);
        const newMonthDate = new Date(y, m - 1, 1);
        setMonthDate(newMonthDate);
        setWeekStart(getWeekStart(form.scheduled_date));
        loadMonth(newMonthDate);
      }
      setView('month');
      setShowForm(false);
      setForm({ sale_id: '', scheduled_date: '', scheduled_time_start: '', crew_notes: '', payment_terms: '' });
      loadAll();
      api.get('/schedule/route-suggest').then(setSuggestions).catch(() => {});
      api.get('/sales?status=confirmed').then(sales => {
        setUnscheduledSales((sales || []).filter(s => s.status === 'confirmed'));
      });
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const markComplete = async (id) => {
    await api.patch(`/schedule/${id}/status`, { status: 'completed' });
    loadWeek();
    loadAll();
    loadMonth();
  };

  const reloadServices = () => api.get('/service').then(d => setServiceJobs(d || [])).catch(() => {});

  const handleScheduleService = async (e) => {
    e.preventDefault();
    if (!svcForm.contact_name?.trim()) { alert('Please select or enter a customer.'); return; }
    if (!svcForm.scheduled_date)        { alert('Please pick a date.'); return; }
    if (!svcForm.description?.trim())   { alert('Please describe the issue.'); return; }
    setSvcSubmitting(true);
    try {
      const payload = {
        ...svcForm,
        sale_id: svcForm.sale_id ? Number(svcForm.sale_id) : null,
        lat: svcForm.lat ? Number(svcForm.lat) : null,
        lng: svcForm.lng ? Number(svcForm.lng) : null,
        status: 'scheduled',
      };
      if (svcForm.id) {
        await api.put(`/service/${svcForm.id}`, payload);
      } else {
        await api.post('/service', payload);
      }
      // Navigate to the month of the scheduled date
      const [y, m] = svcForm.scheduled_date.split('-').map(Number);
      const newMonthDate = new Date(y, m - 1, 1);
      setMonthDate(newMonthDate);
      setWeekStart(getWeekStart(svcForm.scheduled_date));
      loadMonth(newMonthDate);
      setView('month');
      setShowForm(false);
      setSvcForm(emptySvcForm);
      setSvcSaleSearch('');
      reloadServices();
    } catch (err) {
      alert(err.message);
    } finally {
      setSvcSubmitting(false);
    }
  };

  const pickServiceSale = (sale) => {
    setSvcForm(f => ({
      ...f,
      sale_id: sale.id,
      contact_name: sale.customer_name,
      address: sale.customer_address || '',
      city: sale.city || sale.customer_city || '',
      phone: sale.customer_phone || '',
      lat: sale.customer_lat || '',
      lng: sale.customer_lng || '',
    }));
    setSvcSaleSearch('');
  };

  const openServiceScheduleForm = (svc) => {
    setSvcForm({
      id: svc.id,
      sale_id: svc.sale_id || '',
      contact_name: svc.display_name || svc.contact_name || '',
      address: svc.display_address || svc.address || '',
      city: svc.display_city || svc.city || '',
      province: svc.province || 'ON',
      lat: svc.display_lat || svc.lat || '',
      lng: svc.display_lng || svc.lng || '',
      phone: svc.display_phone || svc.phone || '',
      service_type: svc.service_type || 'service',
      description: svc.description || '',
      crew_notes: svc.crew_notes || '',
      scheduled_date: svc.scheduled_date || '',
      scheduled_time_start: svc.scheduled_time_start || '',
    });
    setSvcCustomerMode(svc.sale_id ? 'existing' : 'new');
    setFormType('service');
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const saleDirection = routeSuggestions?.sale?.direction;
  const saleDistKm = routeSuggestions?.sale?.distanceKm;

  const inputCls = 'w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-nova-500 focus:border-nova-500 outline-none';

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Schedule</h1>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border overflow-hidden text-sm">
            <button onClick={() => setView('month')}
              className={`px-3 py-2 flex items-center gap-1.5 ${view === 'month' ? 'bg-nova-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              <LayoutGrid className="w-4 h-4" /> Month
            </button>
            <button onClick={() => setView('week')}
              className={`px-3 py-2 flex items-center gap-1.5 ${view === 'week' ? 'bg-nova-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              <Calendar className="w-4 h-4" /> Week
            </button>
            <button onClick={() => setView('list')}
              className={`px-3 py-2 flex items-center gap-1.5 ${view === 'list' ? 'bg-nova-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              <List className="w-4 h-4" /> List
            </button>
            <button onClick={() => setView('map')}
              className={`px-3 py-2 text-sm flex items-center gap-1.5 ${view === 'map' ? 'bg-nova-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              <MapPin className="w-4 h-4" /> Map
            </button>
          </div>
          <button onClick={() => setShowForm(!showForm)}
            className="bg-nova-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-nova-700 flex items-center gap-2">
            <Plus className="w-4 h-4" /> Schedule Job
            {suggestions.reduce((n, g) => n + g.jobs.length, 0) > 0 && (
              <span className="bg-white text-nova-600 text-xs font-bold px-1.5 py-0.5 rounded-full leading-none">
                {suggestions.reduce((n, g) => n + g.jobs.length, 0)}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Schedule form */}
      {showForm && (
        <div className={`bg-white rounded-xl border p-5 mb-4 ${formType === 'service' ? 'border-orange-300' : ''}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <h2 className="font-semibold">Schedule {formType === 'service' ? 'Service Job' : 'Installation'}</h2>
              <div className="flex rounded-lg border overflow-hidden text-xs">
                <button type="button" onClick={() => setFormType('install')}
                  className={`px-3 py-1.5 font-medium ${formType === 'install' ? 'bg-nova-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                  Install
                </button>
                <button type="button" onClick={() => setFormType('service')}
                  className={`px-3 py-1.5 font-medium flex items-center gap-1 ${formType === 'service' ? 'bg-orange-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                  🔧 Service
                </button>
              </div>
            </div>
            <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
          </div>

          {formType === 'service' ? (
            <form onSubmit={handleScheduleService}>
              {/* Customer section */}
              <div className="bg-gray-50 rounded-xl p-4 mb-3">
                <p className="text-xs font-semibold text-gray-700 mb-2">Customer</p>
                <div className="flex rounded-lg border overflow-hidden text-xs mb-3 w-fit">
                  <button type="button" onClick={() => setSvcCustomerMode('existing')}
                    className={`px-3 py-1.5 font-medium ${svcCustomerMode === 'existing' ? 'bg-orange-500 text-white' : 'bg-white text-gray-600'}`}>
                    Existing Customer
                  </button>
                  <button type="button" onClick={() => { setSvcCustomerMode('new'); setSvcForm(f => ({ ...f, sale_id: '' })); }}
                    className={`px-3 py-1.5 font-medium ${svcCustomerMode === 'new' ? 'bg-orange-500 text-white' : 'bg-white text-gray-600'}`}>
                    New Customer
                  </button>
                </div>

                {svcCustomerMode === 'existing' ? (
                  <div>
                    {svcForm.contact_name && svcForm.sale_id ? (
                      <div className="flex items-center justify-between bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                        <div>
                          <p className="font-semibold text-sm text-orange-800">{svcForm.contact_name}</p>
                          {svcForm.city && <p className="text-xs text-orange-600">{svcForm.address ? `${svcForm.address}, ` : ''}{svcForm.city}</p>}
                        </div>
                        <button type="button"
                          onClick={() => setSvcForm(f => ({ ...f, sale_id: '', contact_name: '', address: '', city: '', phone: '', lat: '', lng: '' }))}
                          className="text-orange-400 hover:text-orange-600"><X className="w-4 h-4" /></button>
                      </div>
                    ) : (
                      <>
                        <input className={inputCls} placeholder="Search installed customers by name or city…"
                          value={svcSaleSearch} onChange={e => setSvcSaleSearch(e.target.value)} autoComplete="off" />
                        {svcSaleSearch && (
                          <ul className="mt-1 border rounded-lg bg-white shadow-sm max-h-48 overflow-auto text-sm">
                            {installedSales.filter(s => {
                              const q = svcSaleSearch.toLowerCase();
                              return s.customer_name?.toLowerCase().includes(q) || s.city?.toLowerCase().includes(q);
                            }).slice(0, 10).map(s => (
                              <li key={s.id} className="px-3 py-2 hover:bg-orange-50 cursor-pointer border-b last:border-0"
                                onClick={() => pickServiceSale(s)}>
                                <span className="font-medium">{s.customer_name}</span>
                                <span className="text-gray-400 text-xs ml-2">{s.city} · {s.total_footage}ft</span>
                              </li>
                            ))}
                            {installedSales.filter(s => {
                              const q = svcSaleSearch.toLowerCase();
                              return s.customer_name?.toLowerCase().includes(q) || s.city?.toLowerCase().includes(q);
                            }).length === 0 && (
                              <li className="px-3 py-2 text-xs text-gray-400">No matches.</li>
                            )}
                          </ul>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <input className={inputCls} placeholder="Contact name *"
                      value={svcForm.contact_name} onChange={e => setSvcForm(f => ({ ...f, contact_name: e.target.value }))} required />
                    <SvcAddressAutocomplete inputCls={inputCls} onSelect={({ address, city, province, lat, lng }) => {
                      setSvcForm(f => ({ ...f, address, city, province, lat, lng }));
                    }} />
                    <div className="grid grid-cols-2 gap-2">
                      <input className={inputCls} placeholder="Street address"
                        value={svcForm.address} onChange={e => setSvcForm(f => ({ ...f, address: e.target.value, lat: '', lng: '' }))} />
                      <input className={inputCls} placeholder="City"
                        value={svcForm.city} onChange={e => setSvcForm(f => ({ ...f, city: e.target.value }))} />
                    </div>
                    <input className={inputCls} placeholder="Phone"
                      value={svcForm.phone} onChange={e => setSvcForm(f => ({ ...f, phone: e.target.value }))} />
                  </div>
                )}
              </div>

              {/* Service details */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Type</label>
                  <select className={inputCls} value={svcForm.service_type}
                    onChange={e => setSvcForm(f => ({ ...f, service_type: e.target.value }))}>
                    <option value="service">Service Call</option>
                    <option value="repair">Repair</option>
                    <option value="warranty">Warranty</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Date *</label>
                  <input type="date" className={inputCls} value={svcForm.scheduled_date}
                    onChange={e => setSvcForm(f => ({ ...f, scheduled_date: e.target.value }))} required />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Start Time</label>
                  <input type="time" className={inputCls} value={svcForm.scheduled_time_start}
                    onChange={e => setSvcForm(f => ({ ...f, scheduled_time_start: e.target.value }))} />
                </div>
                <div className="md:col-span-3">
                  <label className="block text-xs text-gray-500 mb-1">Description / Issue *</label>
                  <textarea className={`${inputCls} h-16`} value={svcForm.description}
                    onChange={e => setSvcForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="What needs to be done?" required />
                </div>
                <div className="md:col-span-3">
                  <label className="block text-xs text-gray-500 mb-1">Crew Notes</label>
                  <input className={inputCls} value={svcForm.crew_notes}
                    onChange={e => setSvcForm(f => ({ ...f, crew_notes: e.target.value }))}
                    placeholder="Gate code, access info…" />
                </div>
              </div>

              <div className="flex gap-2">
                <button type="submit" disabled={svcSubmitting}
                  className="px-5 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50">
                  {svcSubmitting ? 'Scheduling…' : svcForm.id ? 'Save Changes' : 'Schedule Service'}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setSvcForm(emptySvcForm); }}
                  className="px-5 py-2 bg-white border text-gray-600 rounded-lg text-sm hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            </form>
          ) : (
          <form onSubmit={handleSchedule}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-500 mb-1">Sale</label>
                <select className={inputCls} value={form.sale_id}
                  onChange={e => setForm(f => ({ ...f, sale_id: e.target.value }))} required>
                  <option value="">Select confirmed sale…</option>
                  {unscheduledSales.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.customer_name} — {s.total_footage}ft {s.color_name || ''} ({s.city || 'no city'})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Date</label>
                <input type="date" className={inputCls} value={form.scheduled_date}
                  onChange={e => setForm(f => ({ ...f, scheduled_date: e.target.value }))} required />
              </div>
            </div>

            {/* ── Route suggestions ── */}
            {form.sale_id && (
              <div className="mb-3">
                {loadingRoute && (
                  <p className="text-xs text-gray-400 animate-pulse">Finding best dates…</p>
                )}
                {!loadingRoute && routeSuggestions?.suggestions?.length > 0 && (
                  <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3">
                    <p className="text-xs font-semibold text-indigo-700 mb-2 flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5" /> Route suggestions — click to pick a date
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {routeSuggestions.suggestions.slice(0, 6).map((s, i) => {
                        const isActive = form.scheduled_date === s.date;
                        const typeStyles = s.type === 'route_match'
                          ? 'bg-green-100 border-green-300 text-green-800'
                          : s.type === 'ideal'
                          ? 'bg-blue-100 border-blue-300 text-blue-800'
                          : 'bg-white border-gray-200 text-gray-700';
                        const typeIcon = s.type === 'route_match' ? '🛣️' : s.type === 'ideal' ? '⭐' : '📅';
                        return (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setForm(f => ({ ...f, scheduled_date: s.date }))}
                            className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                              isActive
                                ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm'
                                : typeStyles + ' hover:border-indigo-400'
                            }`}
                          >
                            <span className="mr-1">{typeIcon}</span>
                            {formatDateFull(s.date)}
                            {s.same_city && <span className="ml-1 opacity-75">· same area</span>}
                            {s.reasons?.[0] && !s.same_city && (
                              <span className="ml-1 opacity-75">· {s.reasons[0]}</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    {routeSuggestions.sale?.city && (
                      <p className="text-xs text-indigo-500 mt-2">
                        Job location: <span className="font-medium">{routeSuggestions.sale.city}</span>
                        {routeSuggestions.sale.total_footage > 0 && ` · ${routeSuggestions.sale.total_footage} ft`}
                      </p>
                    )}
                    <div className="flex items-center justify-between mt-1 flex-wrap gap-1">
                      {saleDistKm && (
                        <p className="text-xs text-gray-400">
                          Job is {saleDistKm} km {saleDirection} from <span className="font-medium text-gray-500">{geocodeStatus?.homeBase?.name || 'base'}</span>
                        </p>
                      )}
                      <button type="button" onClick={() => setEditingBase(v => !v)}
                        className="text-xs text-nova-500 hover:underline flex items-center gap-0.5">
                        <MapPin className="w-3 h-3" /> {editingBase ? 'Cancel' : 'Change base location'}
                      </button>
                    </div>
                    {editingBase && (
                      <div className="mt-2 relative">
                        <input
                          className="w-full px-3 py-2 border rounded-lg text-xs focus:ring-2 focus:ring-nova-500 outline-none"
                          placeholder="Search your base city or address…"
                          value={baseSearch}
                          onChange={e => searchBase(e.target.value)}
                          autoFocus
                        />
                        {baseResults.length > 0 && (
                          <ul className="absolute z-50 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-48 overflow-auto text-xs">
                            {baseResults.map((f, i) => {
                              const p = f.properties;
                              const label = [p.name || ((p.housenumber || '') + ' ' + (p.street || '')).trim(), p.city || p.town, p.state].filter(Boolean).join(', ');
                              return (
                                <li key={i} onMouseDown={() => pickBase(f)}
                                  className="px-3 py-2 hover:bg-nova-50 cursor-pointer border-b last:border-0">
                                  {label}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {!loadingRoute && routeSuggestions && !routeSuggestions.suggestions?.length && (
                  <p className="text-xs text-gray-400">No route suggestions found — pick any date above.</p>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Start Time</label>
                <input type="time" className={inputCls} value={form.scheduled_time_start}
                  onChange={e => setForm(f => ({ ...f, scheduled_time_start: e.target.value }))} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-500 mb-1">Payment Terms (for ops)</label>
                <input className={inputCls} value={form.payment_terms}
                  onChange={e => setForm(f => ({ ...f, payment_terms: e.target.value }))}
                  placeholder="Auto-filled — edit if needed" />
              </div>
              <div className="md:col-span-3">
                <label className="block text-xs text-gray-500 mb-1">Crew Notes</label>
                <input className={inputCls} placeholder="Gate code, special instructions…"
                  value={form.crew_notes} onChange={e => setForm(f => ({ ...f, crew_notes: e.target.value }))} />
              </div>
            </div>

            <div className="flex gap-2">
              <button type="submit" disabled={submitting}
                className="px-5 py-2 bg-nova-600 text-white rounded-lg text-sm font-medium hover:bg-nova-700 disabled:opacity-50">
                {submitting ? 'Scheduling…' : 'Confirm Schedule'}
              </button>
              <button type="button" onClick={() => setShowForm(false)}
                className="px-5 py-2 bg-white border text-gray-600 rounded-lg text-sm hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </form>
          )}
        </div>
      )}

      {/* ── PENDING SERVICES (Ready to Schedule) ── */}
      {serviceJobs.filter(s => !s.scheduled_date && s.status === 'pending').length > 0 && !showForm && (
        <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-orange-800 flex items-center gap-2">
              <span className="text-lg">🔧</span>
              Pending Services
              <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {serviceJobs.filter(s => !s.scheduled_date && s.status === 'pending').length}
              </span>
            </h2>
            <button type="button"
              onClick={() => { setFormType('service'); setSvcForm(emptySvcForm); setShowForm(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className="text-xs text-orange-600 hover:underline font-medium">
              + New service
            </button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {serviceJobs.filter(s => !s.scheduled_date && s.status === 'pending').map(svc => (
              <div key={svc.id} className="bg-white rounded-lg border border-orange-100 p-3 flex items-center justify-between gap-2 hover:border-orange-300 transition-colors">
                <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setViewingServiceId(svc.id)}>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-semibold text-sm text-orange-700 truncate">🔧 {svc.display_name || svc.contact_name}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 shrink-0 capitalize">
                      {svc.service_type === 'service' ? 'Service' : svc.service_type}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                    <MapPin className="w-3 h-3 shrink-0" />
                    {svc.display_city || 'No city'}
                  </p>
                  {svc.description && <p className="text-xs text-gray-500 mt-0.5 truncate">{svc.description}</p>}
                </div>
                <button onClick={() => openServiceScheduleForm(svc)}
                  className="shrink-0 flex items-center gap-1 bg-orange-500 text-white text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-orange-600 transition-colors">
                  Schedule <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── READY TO SCHEDULE ── */}
      {suggestions.length > 0 && !showForm && (
        <div className="bg-gradient-to-r from-nova-50 to-indigo-50 border border-nova-200 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-nova-800 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-nova-600" />
              Ready to Schedule
              <span className="bg-nova-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {suggestions.reduce((n, g) => n + g.jobs.length, 0)}
              </span>
            </h2>
            <span className="text-xs text-nova-600 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> Base: <span className="font-medium">{geocodeStatus?.homeBase?.name || 'London, ON'}</span>
            </span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {suggestions.flatMap(group =>
              group.jobs.map(job => (
                <div key={job.id} className="bg-white rounded-lg border border-nova-100 p-3 flex items-center justify-between gap-2 hover:border-nova-300 transition-colors">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Link to={`/sales/${job.id}`} onClick={e => e.stopPropagation()}
                        className="font-semibold text-sm text-nova-700 hover:underline truncate">
                        {job.customer_name}
                      </Link>
                      <span className="text-xs text-gray-400 shrink-0">{job.total_footage}ft</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                      <MapPin className="w-3 h-3 shrink-0" />
                      {job.city || 'No city'}
                      {job.install_window && <span className="ml-1 text-blue-500">· {job.install_window}</span>}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setForm(f => ({ ...f, sale_id: String(job.id), scheduled_date: '' }));
                      setShowForm(true);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="shrink-0 flex items-center gap-1 bg-nova-600 text-white text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-nova-700 transition-colors"
                  >
                    Schedule <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ── MONTH VIEW ── */}
      {view === 'month' && (
        <>
          {/* Month nav */}
          <div className="flex items-center gap-3 mb-3">
            <button onClick={() => shiftMonth(-1)} className="p-2 bg-white border rounded-lg hover:bg-gray-50">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="font-semibold text-base flex-1 text-center">
              {MONTH_NAMES[monthNum - 1]} {monthYear}
            </span>
            <button onClick={goToday} className="px-3 py-1.5 text-xs bg-white border rounded-lg hover:bg-gray-50 font-medium">
              Today
            </button>
            <button onClick={() => shiftMonth(1)} className="p-2 bg-white border rounded-lg hover:bg-gray-50">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Day-of-week header */}
          <div className="grid grid-cols-7 gap-px mb-px">
            {dayNames.map(d => (
              <div key={d} className="bg-gray-50 border text-center text-xs font-semibold text-gray-500 py-2 rounded-t">
                {d}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-px bg-gray-200 border border-gray-200 rounded-b-xl overflow-hidden mb-6">
            {monthCells.map(({ dateStr, current }) => {
              const dayJobs = jobsByDate[dateStr] || [];
              const daySvcs = svcByDate[dateStr] || [];
              const isToday = dateStr === today;
              const isPast = dateStr < today;
              const totalFt = dayJobs.reduce((s, j) => s + (j.total_footage || 0), 0);
              const hasHighlight = dayJobs.some(j => j.sale_id === highlightSaleId);
              const [,, dayNum] = dateStr.split('-');

              return (
                <div
                  key={dateStr}
                  ref={hasHighlight ? highlightRef : null}
                  className={`bg-white min-h-[100px] p-1.5 flex flex-col ${
                    !current ? 'bg-gray-50' : ''
                  } ${hasHighlight ? 'ring-2 ring-inset ring-amber-400' : ''}`}
                >
                  {/* Day number + capacity badge */}
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-semibold w-6 h-6 flex items-center justify-center rounded-full ${
                      isToday
                        ? 'bg-nova-600 text-white'
                        : !current
                        ? 'text-gray-300'
                        : isPast
                        ? 'text-gray-400'
                        : 'text-gray-700'
                    }`}>
                      {parseInt(dayNum, 10)}
                    </span>
                    {totalFt > 0 && (
                      <span className={`text-[10px] font-medium px-1 py-0.5 rounded ${
                        totalFt >= 250 && totalFt <= 400 ? 'bg-green-100 text-green-700' :
                        totalFt > 400 ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>{totalFt}ft</span>
                    )}
                  </div>

                  {/* Job pills */}
                  <div className="flex flex-col gap-0.5 flex-1">
                    {dayJobs.map(job => {
                      const isHl = job.sale_id === highlightSaleId;
                      return (
                        <Link
                          key={job.id}
                          to={`/sales/${job.sale_id}`}
                          className={`text-[11px] leading-tight px-1.5 py-0.5 rounded truncate font-medium ${
                            isHl
                              ? 'bg-amber-200 text-amber-900'
                              : job.status === 'completed'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-nova-100 text-nova-800 hover:bg-nova-200'
                          }`}
                          title={`${job.customer_name} · ${job.total_footage}ft · ${job.customer_city || ''}`}
                        >
                          {job.customer_name}{job.customer_city ? ` · ${job.customer_city}` : ''}
                        </Link>
                      );
                    })}
                    {daySvcs.map(svc => (
                      <button key={`svc-${svc.id}`}
                        onClick={() => setViewingServiceId(svc.id)}
                        className="text-[11px] leading-tight px-1.5 py-0.5 rounded truncate font-medium bg-orange-100 text-orange-800 hover:bg-orange-200 text-left"
                        title={`🔧 ${svc.display_name || svc.contact_name} · ${svc.service_type}`}>
                        🔧 {svc.display_name || svc.contact_name}
                      </button>
                    ))}
                  </div>

                  {/* Click empty day to schedule */}
                  {current && !isPast && dayJobs.length === 0 && (
                    <button
                      onClick={() => {
                        setForm(f => ({ ...f, scheduled_date: dateStr }));
                        setShowForm(true);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="mt-auto text-[10px] text-gray-300 hover:text-nova-500 text-center w-full opacity-0 hover:opacity-100 transition-opacity"
                    >
                      + Add
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── WEEK VIEW ── */}
      {view === 'week' && (
        <>
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => shiftWeek(-1)} className="p-2 bg-white border rounded-lg hover:bg-gray-50">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="font-medium text-sm flex-1 text-center">
              {formatDate(days[0])} — {formatDate(days[6])}
            </span>
            <button onClick={goToday} className="px-3 py-1.5 text-xs bg-white border rounded-lg hover:bg-gray-50 font-medium">
              Today
            </button>
            <button onClick={() => shiftWeek(1)} className="p-2 bg-white border rounded-lg hover:bg-gray-50">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-7 gap-2 mb-8">
            {days.map((date, i) => {
              const dayJobs = jobs.filter(j => j.scheduled_date === date);
              const daySvcJobs = serviceJobs.filter(s => s.scheduled_date === date && s.status === 'scheduled');
              const totalFt = dayJobs.reduce((sum, j) => sum + (j.total_footage || 0), 0);
              const isToday = date === today;
              return (
                <div key={date} className={`bg-white rounded-xl border p-3 min-h-[140px] ${isToday ? 'ring-2 ring-nova-500' : ''}`}>
                  <div className="flex justify-between items-center mb-2">
                    <span className={`text-xs font-semibold ${isToday ? 'text-nova-600' : 'text-gray-500'}`}>{dayNames[i]}</span>
                    <span className={`text-xs ${isToday ? 'text-nova-500 font-medium' : 'text-gray-400'}`}>{formatDate(date)}</span>
                  </div>
                  {totalFt > 0 && (
                    <div className={`text-xs mb-2 px-2 py-0.5 rounded-full inline-block font-medium ${
                      totalFt >= 250 && totalFt <= 400 ? 'bg-green-100 text-green-700' :
                      totalFt > 400 ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>{totalFt}ft</div>
                  )}
                  {dayJobs.length === 0 && daySvcJobs.length === 0 && (
                    <p className="text-xs text-gray-300 mt-4 text-center">—</p>
                  )}
                  {dayJobs.map(job => {
                    const isHl = job.sale_id === highlightSaleId;
                    return (
                      <div
                        key={job.id}
                        ref={isHl ? highlightRef : null}
                        className={`rounded-lg p-2 mb-1.5 text-xs border-l-4 ${isHl ? 'bg-amber-50 border-amber-400 ring-1 ring-amber-300' : 'bg-gray-50 border-nova-400'}`}
                      >
                        <Link to={`/sales/${job.sale_id}`} className="font-semibold text-nova-700 hover:underline truncate block">
                          {job.customer_name}
                        </Link>
                        <p className="text-gray-600">{job.total_footage}ft · {job.color_name || '—'}</p>
                        {(job.customer_address || job.customer_city) && (
                          <p className="text-gray-400 truncate flex items-start gap-1">
                            <MapPin className="w-2.5 h-2.5 mt-0.5 shrink-0" />
                            <span>{[job.customer_address, job.customer_city].filter(Boolean).join(', ')}</span>
                          </p>
                        )}
                        {job.customer_phone && (
                          <p className="text-gray-400 flex items-center gap-1"><Phone className="w-2.5 h-2.5" />{job.customer_phone}</p>
                        )}
                        {job.payment_terms && (
                          <p className="mt-1 px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-[10px] leading-tight">{job.payment_terms}</p>
                        )}
                        {job.crew_notes && (
                          <p className="mt-0.5 text-gray-400 italic text-[10px]">{job.crew_notes}</p>
                        )}
                        {job.status === 'scheduled' && (
                          <button onClick={() => markComplete(job.id)}
                            className="mt-1.5 flex items-center gap-1 text-green-600 hover:text-green-700 text-[10px] font-medium">
                            <CheckCircle className="w-3 h-3" /> Mark Complete
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {daySvcJobs.map(svc => (
                    <button key={`svc-${svc.id}`}
                      onClick={() => setViewingServiceId(svc.id)}
                      className="w-full text-left rounded-lg p-2 mb-1.5 text-xs border-l-4 bg-orange-50 border-orange-400 hover:bg-orange-100">
                      <p className="font-semibold text-orange-700 truncate flex items-center gap-1">
                        🔧 {svc.display_name || svc.contact_name}
                      </p>
                      <p className="text-orange-500 capitalize">{svc.service_type === 'service' ? 'Service Call' : svc.service_type}</p>
                      {svc.display_city && (
                        <p className="text-gray-400 truncate flex items-center gap-1">
                          <MapPin className="w-2.5 h-2.5 shrink-0" />{svc.display_city}
                        </p>
                      )}
                      {svc.description && <p className="text-gray-400 truncate mt-0.5">{svc.description}</p>}
                      {svc.crew_notes && <p className="text-gray-400 italic text-[10px] mt-0.5">{svc.crew_notes}</p>}
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── LIST VIEW ── */}
      {view === 'list' && (() => {
        // Merge install jobs + scheduled service jobs, sorted by date
        const upcomingSvcs = serviceJobs.filter(s => s.scheduled_date && s.status === 'scheduled');
        const merged = [
          ...upcomingAll.map(j => ({ ...j, _type: 'install' })),
          ...upcomingSvcs.map(s => ({ ...s, _type: 'service' })),
        ].sort((a, b) => (a.scheduled_date || '').localeCompare(b.scheduled_date || ''));
        return (
        <div className="bg-white rounded-xl border overflow-hidden mb-8">
          <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
            <h2 className="font-semibold text-sm">Upcoming Jobs</h2>
            <span className="text-xs text-gray-400">{merged.length} job{merged.length !== 1 ? 's' : ''}</span>
          </div>
          {merged.length === 0 ? (
            <p className="p-8 text-center text-gray-400 text-sm">No upcoming jobs scheduled</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Date</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Customer</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Details</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Payment / Notes</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Status</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {merged.map(item => {
                  if (item._type === 'service') {
                    const isPast = item.scheduled_date < today;
                    return (
                      <tr key={`svc-${item.id}`}
                        className="hover:bg-orange-50/40 bg-orange-50/20 cursor-pointer"
                        onClick={() => setViewingServiceId(item.id)}>
                        <td className="px-4 py-3">
                          <p className="font-medium">{formatDateFull(item.scheduled_date)}</p>
                          {item.scheduled_time_start && <p className="text-xs text-gray-400">{item.scheduled_time_start}</p>}
                          {isPast && <span className="text-xs text-yellow-600 font-medium">Overdue</span>}
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-orange-700 flex items-center gap-1 hover:underline">🔧 {item.display_name || item.contact_name}</p>
                          {item.display_city && (
                            <p className="text-xs text-gray-400 flex items-center gap-1">
                              <MapPin className="w-3 h-3 shrink-0" />{item.display_city}
                            </p>
                          )}
                          {item.display_phone && <p className="text-xs text-gray-400">{item.display_phone}</p>}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 capitalize font-medium">
                            {item.service_type === 'service' ? 'Service Call' : item.service_type}
                          </span>
                          {item.description && <p className="text-xs text-gray-500 mt-0.5 truncate max-w-[180px]">{item.description}</p>}
                        </td>
                        <td className="px-4 py-3">
                          {item.crew_notes
                            ? <span className="text-xs text-gray-500 italic">{item.crew_notes}</span>
                            : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 font-medium">Service</span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              await api.patch(`/service/${item.id}/status`, { status: 'completed' });
                              reloadServices();
                            }}
                            className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 font-medium whitespace-nowrap">
                            <CheckCircle className="w-3.5 h-3.5" /> Complete
                          </button>
                        </td>
                      </tr>
                    );
                  }
                  // Install job
                  const job = item;
                  const isPast = job.scheduled_date < today;
                  const isHl = job.sale_id === highlightSaleId;
                  return (
                    <tr
                      key={`job-${job.id}`}
                      ref={isHl ? highlightRef : null}
                      className={`hover:bg-gray-50 ${isHl ? 'bg-amber-50 ring-2 ring-inset ring-amber-400' : isPast && job.status === 'scheduled' ? 'bg-yellow-50' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <p className="font-medium">{formatDateFull(job.scheduled_date)}</p>
                        {job.scheduled_time_start && <p className="text-xs text-gray-400">{job.scheduled_time_start}</p>}
                        {isPast && job.status === 'scheduled' && (
                          <span className="text-xs text-yellow-600 font-medium">Overdue</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Link to={`/sales/${job.sale_id}`} className="font-medium text-nova-600 hover:underline">{job.customer_name}</Link>
                        {(job.customer_address || job.customer_city) && (
                          <p className="text-xs text-gray-400 flex items-center gap-1">
                            <MapPin className="w-3 h-3 shrink-0" />
                            {[job.customer_address, job.customer_city].filter(Boolean).join(', ')}
                          </p>
                        )}
                        {job.customer_phone && <p className="text-xs text-gray-400">{job.customer_phone}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <p>{job.total_footage}ft · {job.color_name || '—'}</p>
                        {job.crew_notes && <p className="text-xs text-gray-400 italic">{job.crew_notes}</p>}
                      </td>
                      <td className="px-4 py-3">
                        {job.payment_terms
                          ? <span className="text-xs px-2 py-1 bg-amber-50 text-amber-700 rounded">{job.payment_terms}</span>
                          : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={job.status} /></td>
                      <td className="px-4 py-3">
                        {job.status === 'scheduled' && (
                          <button onClick={() => markComplete(job.id)}
                            className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 font-medium whitespace-nowrap">
                            <CheckCircle className="w-3.5 h-3.5" /> Complete
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        );
      })()}

      {/* ── MAP VIEW ── */}
      {view === 'map' && (() => {
        // Scheduled jobs from /schedule endpoint (have customer_lat/lng) — exclude completed/collected
        const completedIds = new Set(allSales.filter(s => s.status === 'completed').map(s => s.id));
        const scheduledOnMap = upcomingAll.filter(j => j.customer_lat && j.customer_lng && !completedIds.has(j.sale_id));
        // Date → color for scheduled install jobs (orange reserved for service)
        const dateColors = {};
        const palette = ['#3b82f6','#8b5cf6','#a855f7','#eab308','#ec4899','#14b8a6','#6366f1'];
        [...new Set(scheduledOnMap.map(j => j.scheduled_date))].sort().forEach((d, i) => {
          dateColors[d] = palette[i % palette.length];
        });

        // Pending/color_match/confirmed sales with coordinates (not yet scheduled)
        const scheduledSaleIds = new Set(upcomingAll.map(j => j.sale_id));
        const unscheduledOnMap = allSales.filter(s =>
          s.customer_lat && s.customer_lng &&
          ['pending','color_match','confirmed'].includes(s.status) &&
          !scheduledSaleIds.has(s.id)
        );

        const dotColor = (status) => {
          if (status === 'pending' || status === 'color_match') return '#ef4444'; // red
          if (status === 'confirmed') return '#22c55e'; // green
          return '#6b7280';
        };

        // Service jobs with coordinates (pending + scheduled, not completed)
        const serviceOnMap = serviceJobs.filter(s =>
          s.display_lat && s.display_lng && s.status !== 'completed' && s.status !== 'cancelled'
        );

        const allVisible = scheduledOnMap.length + unscheduledOnMap.length + serviceOnMap.length;

        return (
          <div className="space-y-3">
            {geocodeStatus && geocodeStatus.missing > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center justify-between">
                <span className="text-sm text-amber-700">
                  {geocodeStatus.missing} customer{geocodeStatus.missing !== 1 ? 's' : ''} not yet mapped ({geocodeStatus.geocoded}/{geocodeStatus.total} geocoded)
                </span>
                <button onClick={handleGeocodeAll} disabled={geocoding}
                  className="text-sm bg-amber-600 text-white px-3 py-1.5 rounded-lg hover:bg-amber-700 disabled:opacity-50">
                  {geocoding ? 'Geocoding…' : 'Geocode All'}
                </button>
              </div>
            )}
            {allVisible === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center text-gray-400">
                <MapPin className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p>No geocoded customers to display. Click "Geocode All" to map your customers.</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border overflow-hidden" style={{ height: 520 }}>
                <MapContainer
                  center={[42.9849, -81.2453]}
                  zoom={9}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  />
                  {/* Unscheduled sales — red (pending/color_match) or green (confirmed) */}
                  {unscheduledOnMap.map(sale => (
                    <Marker
                      key={`sale-${sale.id}`}
                      position={[sale.customer_lat, sale.customer_lng]}
                      icon={L.divIcon({
                        className: '',
                        html: `<div style="width:12px;height:12px;border-radius:50%;background:${dotColor(sale.status)};border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.4)"></div>`,
                        iconSize: [12, 12],
                        iconAnchor: [6, 6],
                      })}
                    >
                      <Popup>
                        <div style={{ minWidth: 160 }}>
                          <p style={{ fontWeight: 600, marginBottom: 2 }}>{sale.customer_name}</p>
                          <p style={{ fontSize: 12, color: '#666' }}>{sale.city} · {sale.total_footage}ft</p>
                          <p style={{ fontSize: 11, marginTop: 4, color: sale.status === 'confirmed' ? '#16a34a' : '#dc2626', fontWeight: 500 }}>
                            {sale.status === 'confirmed' ? 'Confirmed — needs scheduling' : sale.status === 'color_match' ? 'Color match pending' : 'New sale'}
                          </p>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                  {/* Scheduled install jobs — colored by date */}
                  {scheduledOnMap.map(job => (
                    <Marker
                      key={`job-${job.id}`}
                      position={[job.customer_lat, job.customer_lng]}
                      icon={L.divIcon({
                        className: '',
                        html: `<div style="width:14px;height:14px;border-radius:50%;background:${dateColors[job.scheduled_date]};border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.4)"></div>`,
                        iconSize: [14, 14],
                        iconAnchor: [7, 7],
                      })}
                    >
                      <Popup>
                        <div style={{ minWidth: 170 }}>
                          <p style={{ fontWeight: 600, marginBottom: 2 }}>{job.customer_name}</p>
                          <p style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>{formatMapDate(job.scheduled_date)}</p>
                          <p style={{ fontSize: 12, color: '#888' }}>{job.total_footage}ft{job.color_name ? ` · ${job.color_name}` : ''}</p>
                          {job.payment_terms && <p style={{ fontSize: 11, marginTop: 4, color: '#888' }}>{job.payment_terms}</p>}
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                  {/* Service jobs — orange wrench markers */}
                  {serviceOnMap.map(svc => (
                    <Marker
                      key={`svc-${svc.id}`}
                      position={[svc.display_lat, svc.display_lng]}
                      icon={L.divIcon({
                        className: '',
                        html: `<div style="width:18px;height:18px;border-radius:50%;background:#f97316;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;font-size:10px;line-height:1">🔧</div>`,
                        iconSize: [18, 18],
                        iconAnchor: [9, 9],
                      })}
                    >
                      <Popup>
                        <div style={{ minWidth: 170 }}>
                          <p style={{ fontWeight: 600, marginBottom: 2 }}>🔧 {svc.display_name || svc.contact_name}</p>
                          <p style={{ fontSize: 12, color: '#ea580c', fontWeight: 500, marginBottom: 2 }}>
                            {svc.service_type === 'service' ? 'Service Call' : svc.service_type === 'repair' ? 'Repair' : 'Warranty'}
                          </p>
                          {svc.scheduled_date && <p style={{ fontSize: 12, color: '#666' }}>{formatMapDate(svc.scheduled_date)}</p>}
                          {svc.description && <p style={{ fontSize: 11, color: '#888', marginTop: 4 }}>{svc.description}</p>}
                          <p style={{ fontSize: 11, color: svc.status === 'scheduled' ? '#2563eb' : '#ca8a04', marginTop: 4, fontWeight: 500 }}>
                            {svc.status === 'scheduled' ? 'Scheduled' : 'Pending scheduling'}
                          </p>
                          <button
                            onClick={() => setViewingServiceId(svc.id)}
                            style={{ marginTop: 6, fontSize: 11, color: '#ea580c', textDecoration: 'underline', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                            View full details →
                          </button>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
            )}
            {/* Legend */}
            <div className="flex flex-wrap gap-2">
              <span className="flex items-center gap-1.5 text-xs bg-white border rounded-full px-2.5 py-1">
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} />
                New sale
              </span>
              <span className="flex items-center gap-1.5 text-xs bg-white border rounded-full px-2.5 py-1">
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
                Confirmed
              </span>
              {serviceOnMap.length > 0 && (
                <span className="flex items-center gap-1.5 text-xs bg-orange-50 border border-orange-200 rounded-full px-2.5 py-1 text-orange-700">
                  🔧 Service job
                </span>
              )}
              {Object.entries(dateColors).map(([date, color]) => (
                <span key={date} className="flex items-center gap-1.5 text-xs bg-white border rounded-full px-2.5 py-1">
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
                  {formatMapDate(date)}
                </span>
              ))}
            </div>
          </div>
        );
      })()}

      {viewingServiceId && (
        <ServiceDetailDrawer
          serviceId={viewingServiceId}
          onClose={() => setViewingServiceId(null)}
          onChanged={reloadServices}
          onEdit={openServiceScheduleForm}
          userRole={user?.role}
        />
      )}
    </div>
  );
}
